import type { AdminStuckWebhookGroupDto, AdminStuckWebhooksDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { WebhookIcon } from './icons';
import { SectionCard } from './SectionCard';
import { SectionMeta } from './SectionMeta';
import { tokens } from '../../../design';
import { formatDateTime } from '../../../utils/format';
import { thresholdText } from '../_lib/moneyHealthText';

/**
 * «Вебхуки не применились» — rows claimed and never completed, broken down by
 * cause.
 *
 * **The breakdown is the point.** Six stuck rows looked like one incident and
 * were three; a flat count would have hidden that.
 *
 * **There is no action here, not even a benign one.** The obvious "mark as
 * handled" is `processed = true`, and that is the one write we must not offer:
 * `processed` is the sole idempotency signal, so a hand-set `true` makes a
 * genuine redelivery a 200-no-op forever after. The footnote says so, in the
 * UI, so nobody adds the button back in a later pass.
 *
 * `provider` and `eventType` render VERBATIM. They are open sets — a Russian
 * lookup table would render a blank row for the next provider, and it is the
 * raw tag an engineer greps for anyway.
 */
const GroupRow = ({ group }: { group: AdminStuckWebhookGroupDto }): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="webhook-group-row"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2.5,
        px: 3.5,
        py: 1.75,
        borderBottom: `1px solid ${tokens.border.default}`,
        '&:last-of-type': { borderBottom: 0 },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
            lineHeight: '13px',
            color: tokens.text.tertiary,
          }}
        >
          {group.provider}
        </Typography>
        <Typography
          sx={{
            fontFamily: tokens.font.mono,
            fontSize: 12,
            fontWeight: 600,
            lineHeight: '16px',
            color: tokens.text.primary,
          }}
        >
          {group.eventType}
        </Typography>
      </Box>
      <Typography
        sx={{
          ml: 'auto',
          flexShrink: 0,
          fontFamily: tokens.font.mono,
          fontSize: 15,
          fontWeight: 700,
          color: tokens.state.danger,
        }}
      >
        {group.count}
      </Typography>
      <Box sx={{ flexShrink: 0, textAlign: 'right', width: 118 }}>
        <Typography sx={{ fontSize: 10.5, lineHeight: '14px', color: tokens.text.tertiary }}>
          {t('moneyHealth.webhooks.oldest')}
        </Typography>
        <Typography
          sx={{
            fontFamily: tokens.font.mono,
            fontSize: 10.5,
            fontWeight: 600,
            lineHeight: '14px',
            color: tokens.text.secondary,
          }}
        >
          {/*
            Structurally non-null, but the SQL aggregate types it nullable —
            rendered as "неизвестно", NEVER as "только что".
          */}
          {group.oldestCreatedAt === null
            ? t('moneyHealth.webhooks.oldestUnknown')
            : formatDateTime(group.oldestCreatedAt)}
        </Typography>
      </Box>
    </Box>
  );
};

export const StuckWebhooksSection = ({
  webhooks,
  webhookStaleAfterMs,
}: {
  webhooks: AdminStuckWebhooksDto;
  webhookStaleAfterMs: number;
}): ReactElement => {
  const { t } = useTranslation();
  const threshold = thresholdText(webhookStaleAfterMs);

  return (
    <SectionCard
      data-testid="section-stuck-webhooks"
      tone="alert"
      icon={<WebhookIcon />}
      title={t('moneyHealth.webhooks.title')}
      subtitle={t('moneyHealth.webhooks.sub', {
        threshold: t(threshold.key, { ...threshold.values }),
      })}
      // `rowCount` is summed from a full aggregate before capping — always
      // exact, so it never wears the `≥` the hold floors do.
      count={String(webhooks.rowCount)}
      footer={
        <>
          {webhooks.groupsTruncated ? (
            <SectionMeta
              text={{
                key: 'moneyHealth.webhooks.groupsTruncated',
                values: { shown: webhooks.byEventType.length },
              }}
            />
          ) : null}
          <SectionMeta text={{ key: 'moneyHealth.webhooks.note' }} testId="webhooks-no-actions" />
        </>
      }
    >
      {webhooks.byEventType.map((group) => (
        <GroupRow key={`${group.provider}:${group.eventType}`} group={group} />
      ))}
    </SectionCard>
  );
};
